import {
  Checkbox,
  createStyles,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  Tooltip,
} from "@material-ui/core";
import * as React from "react";
import EUser from "../../../core/contracts/EUser";
import { formatDateTime } from "../../../helpers/datetime";
import DeleteIcon from "@material-ui/icons/Delete";
import { isEqual } from "lodash";
import { isArrayValid } from "../../../helpers/utility";
import { Skeleton } from "@material-ui/lab";
import EGroup from "../../../core/contracts/EGroup";
import EUserDirector from "../../../core/contracts/EUserDirector";
import { CardTravel } from "@material-ui/icons";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      "& .MuiTableCell-stickyHeader": {
        backgroundColor: "#1D212A",
        color: theme.palette.common.white,
      },
    },
  })
);

const loadingState = new Array(6).fill({});

type MembersTableProps = {
  rows: EUser[];
  handleDelete: (userCode: string) => void;
  handleSelect: (userCode: string) => void;
  handleDirector: (userCode: string, diretorias?: EUserDirector[]) => void;
  handleSelectAll: () => void;
  selectedRows: string[];
  loading?: boolean;
  group: EGroup | null;
  currentAdmin: EUser | undefined;
};

function MembersTable({
  rows,
  selectedRows,
  handleSelectAll,
  handleSelect,
  handleDelete,
  handleDirector,
  loading = false,
  group,
  currentAdmin,
}: MembersTableProps) {
  const classes = useStyles();

  const renderValue = (value?: string | Date | number | null): React.ReactNode => {
    return value || value === 0 ? value : "-";
  };

  const renderArrayValue = (value?: string[]): React.ReactNode => {
    return value ? (value?.length > 0 ? value?.filter((s) => s !== "").join(", ") : "-") : "-";
  };

  const formatDiretorias = (diretorias?: EUserDirector[]): string[] => {
    if (!diretorias || diretorias?.length === 0) {
      return [];
    }

    return diretorias?.flatMap((d: EUserDirector) => {
      return d.diretoria?.name || "";
    });
  };

  const canEditAdminGroup =
    group?.id === "B3726236-C10A-4DC4-B524-DA2571E2B104".toLowerCase() &&
    !currentAdmin?.super_usuario;

  const onlyAdmin =
    currentAdmin?.super_usuario ||
    currentAdmin?.grupo?.id === "B3726236-C10A-4DC4-B524-DA2571E2B104".toLowerCase();

  const renderRow = (user: EUser) => {
    return (
      <TableRow key={user.codigo}>
        <TableCell padding="checkbox">
          <Checkbox
            disabled={canEditAdminGroup}
            color="secondary"
            onChange={() => handleSelect(user.codigo)}
            checked={selectedRows.includes(user.codigo)}
          />
        </TableCell>
        <TableCell align="center">{renderValue(user?.email)}</TableCell>
        <TableCell align="center">{renderValue(user?.nome)}</TableCell>
        <TableCell align="center">{renderValue(formatDateTime(user?.created_at))}</TableCell>
        <TableCell align="center">{renderValue(user?.adicionado_por)}</TableCell>
        <TableCell align="center">
          {renderArrayValue(formatDiretorias(user?.usuarios_diretorias))}
        </TableCell>
        <TableCell align="center" style={{ width: "130px" }}>
          <Tooltip title="Gerenciar Diretorias">
            <IconButton
              onClick={() => handleDirector(user.email, user.usuarios_diretorias)}
              disabled={!onlyAdmin}
            >
              <CardTravel />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir Usuário">
            <IconButton onClick={() => handleDelete(user.codigo)} disabled={canEditAdminGroup}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  };

  const allRowsIds = React.useMemo(() => rows.map((x) => x.codigo), [rows]);
  const everyRowIsSelected = isEqual(selectedRows, allRowsIds);

  return (
    <Table stickyHeader className={classes.root}>
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox">
            <Checkbox
              disabled={canEditAdminGroup}
              color="secondary"
              checked={isArrayValid(allRowsIds) && everyRowIsSelected}
              onClick={handleSelectAll}
            />
          </TableCell>

          <TableCell align="center">Email</TableCell>
          <TableCell align="center">Nome de usuário</TableCell>
          <TableCell align="center">Data de Inclusão</TableCell>
          <TableCell align="center">Adicionado Por</TableCell>
          <TableCell align="center">Diretorias</TableCell>
          <TableCell align="center" style={{ width: "130px" }}>
            Ações
          </TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {loading
          ? loadingState.map((_, idx) => (
              <TableRow key={`loading_row_${idx}`}>
                <TableCell colSpan={6}>
                  <Skeleton />
                </TableCell>
              </TableRow>
            ))
          : rows.map((x) => renderRow(x))}
      </TableBody>
    </Table>
  );
}

export default MembersTable;
